import { useEffect, useMemo, useRef, useState } from "react";
import QRCode from "qrcode";
import { useI18n } from "../i18n";
import { getSettings, getWall, type BoardSettings, type WallPost } from "../lib/api";
import { themeStyle } from "../lib/theme";
import { EVENT_TITLE_SHORT } from "../../shared/event";
import { Cake } from "../components/Icon";

const WALL_RESHUFFLE_MS = 10000;
const POLL_MS = 12000; // pick up newly-approved cards quickly
const DEFAULT_SETTINGS: BoardSettings = {
  board_card_seconds: 180,
  board_wall_seconds: 120,
  board_card_advance_seconds: 10,
};

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function Board() {
  const { t } = useI18n();
  const [posts, setPosts] = useState<WallPost[]>([]);
  const [settings, setSettings] = useState<BoardSettings>(DEFAULT_SETTINGS);
  const [mode, setMode] = useState<"card" | "wall">("card");
  const [idx, setIdx] = useState(0);
  const [wallSeed, setWallSeed] = useState(0);
  const [qr, setQr] = useState("");
  const newestId = useRef<string | null>(null);

  useEffect(() => {
    const load = () =>
      getWall()
        .then((p) => {
          setPosts(p);
          // When a new card is approved it becomes the newest — jump to it so it
          // shows on the board without waiting for a full rotation.
          if (p.length && p[0].id !== newestId.current) {
            newestId.current = p[0].id;
            setIdx(0);
          }
        })
        .catch(() => {});
    load();
    getSettings().then(setSettings).catch(() => {});
    QRCode.toDataURL(`${window.location.origin}/wall`, { margin: 1, width: 220 }).then(setQr).catch(() => {});
    const poll = setInterval(load, POLL_MS);
    return () => clearInterval(poll);
  }, []);

  // Alternate between single-card and card-wall on the admin-configured timings.
  useEffect(() => {
    if (posts.length === 0) return;
    const seconds = mode === "card" ? settings.board_card_seconds : settings.board_wall_seconds;
    const id = setTimeout(() => setMode((m) => (m === "card" ? "wall" : "card")), seconds * 1000);
    return () => clearTimeout(id);
  }, [mode, settings, posts.length]);

  // Single-card cycling (swap interval is admin-configurable).
  useEffect(() => {
    if (mode !== "card" || posts.length <= 1) return;
    const ms = Math.max(2000, settings.board_card_advance_seconds * 1000);
    const id = setInterval(() => setIdx((i) => (i + 1) % posts.length), ms);
    return () => clearInterval(id);
  }, [mode, posts.length, settings.board_card_advance_seconds]);

  // Card-wall reshuffle.
  useEffect(() => {
    if (mode !== "wall") return;
    const id = setInterval(() => setWallSeed((s) => s + 1), WALL_RESHUFFLE_MS);
    return () => clearInterval(id);
  }, [mode]);

  const current = posts[idx % Math.max(1, posts.length)];
  const wallPosts = useMemo(() => shuffle(posts).slice(0, 15), [posts, wallSeed]);

  return (
    <div style={themeStyle("brand")} className="theme-wash relative flex h-dvh flex-col overflow-hidden">
      <header className="flex items-center justify-between px-10 py-6">
        <div className="inline-flex items-center gap-2 font-display text-3xl font-bold text-[var(--theme-deep)]">
          <Cake className="h-8 w-8" /> HBD Bogummy
        </div>
        <div className="text-lg font-bold text-ink/55">{EVENT_TITLE_SHORT}</div>
      </header>

      <main className="flex flex-1 items-center justify-center overflow-hidden px-10 pb-4">
        {posts.length === 0 || !current ? (
          <p className="max-w-2xl text-center text-2xl font-semibold text-ink/55">{t("board.empty")}</p>
        ) : mode === "card" ? (
          <div key={current.id} className="fade-in flex w-full max-w-5xl flex-col items-center gap-6">
            {current.has_image && (
              <img
                src={`/api/wall/img/${current.id}`}
                alt=""
                className="max-h-[58vh] rounded-3xl object-contain shadow-soft ring-8 ring-white"
              />
            )}
            {current.message && (
              <p className="max-w-3xl text-center text-3xl font-semibold leading-snug text-ink">“{current.message}”</p>
            )}
            {current.display_name && <p className="text-xl font-bold text-[var(--theme-deep)]">— {current.display_name}</p>}
          </div>
        ) : (
          <div key={wallSeed} className="fade-in h-full w-full max-w-6xl columns-2 gap-4 py-2 sm:columns-3 lg:columns-4 [&>*]:mb-4">
            {wallPosts.map((p, i) => (
              <div
                key={p.id}
                className="float-slow break-inside-avoid rounded-2xl bg-white p-3 shadow-card ring-1 ring-black/[0.05]"
                style={{ animationDelay: `${(i % 5) * 0.4}s` }}
              >
                {p.has_image && (
                  <img src={`/api/wall/img/${p.id}`} alt="" className="mb-2 max-h-56 w-full rounded-xl object-cover" />
                )}
                {p.message && <p className="text-sm leading-snug text-ink/80">{p.message}</p>}
                {p.display_name && <p className="mt-1 text-xs font-semibold text-[var(--theme-deep)]">— {p.display_name}</p>}
              </div>
            ))}
          </div>
        )}
      </main>

      <footer className="flex items-center justify-between px-10 py-5">
        <div className="flex items-center gap-4">
          {qr && <img src={qr} alt="QR" className="h-24 w-24 rounded-xl bg-white p-1 shadow-card" />}
          <div className="text-lg font-bold text-ink/70">
            {t("board.scanToPost")}
            <div className="text-sm font-normal text-ink/50">{window.location.host}/wall</div>
          </div>
        </div>
        {mode === "card" && posts.length > 1 && (
          <div className="flex gap-1.5">
            {posts.slice(0, 12).map((_, i) => (
              <span
                key={i}
                className={`h-2 w-2 rounded-full transition ${i === idx % posts.length ? "bg-[var(--theme-deep)]" : "bg-black/15"}`}
              />
            ))}
          </div>
        )}
      </footer>
    </div>
  );
}
